export const RAW_CLASS_LABEL_KEYS: Record<string, string> = {
    mosquito: 'rawClasses.mosquito',
    aedes: 'rawClasses.aedes',
    cualex: 'rawClasses.culex',
    culex: 'rawClasses.culex',
    ppculex: 'rawClasses.culex',
    anopheles: 'rawClasses.anopheles',
    mosfragment: 'rawClasses.mosfragment',
    midgerr: 'rawClasses.midgerr',
    pyralid: 'rawClasses.pyralid',
    drain_fly: 'rawClasses.drainFly',
    moth: 'rawClasses.moth',
    insect: 'rawClasses.insect',
    cabbage: 'rawClasses.cabbage',
    other: 'rawClasses.other',
    unknown: 'rawClasses.unknown',
}

type TranslateRawClass = (
    key: string,
    options?: { defaultValue?: string },
) => string

const SUMMARY_SEGMENT_PATTERN = /^(.+?)(\((\d+)\))$/

interface RawClassSummaryFormatOptions {
    withCode?: boolean
    localizedNames?: Record<string, string>
}

const resolvePreferredLocalizedName = (
    rawClass: string,
    t: TranslateRawClass,
    preferredName?: string,
): string => {
    const normalizedPreferredName = preferredName?.trim()
    if (
        normalizedPreferredName &&
        normalizedPreferredName.toLowerCase() !== rawClass.trim().toLowerCase()
    ) {
        return normalizedPreferredName
    }
    return getRawClassLocalizedName(rawClass, t)
}

export const getRawClassLocalizedName = (
    rawClass: string,
    t: TranslateRawClass,
): string => {
    const normalizedRawClass = rawClass.trim().toLowerCase()
    const labelKey = RAW_CLASS_LABEL_KEYS[normalizedRawClass]
    if (!labelKey) {
        return rawClass
    }
    return t(`admin:modelManagement.${labelKey}`, { defaultValue: rawClass })
}

export const formatRawClassDisplayName = (
    rawClass: string,
    t: TranslateRawClass,
    preferredName?: string,
): string => {
    const localizedName = resolvePreferredLocalizedName(rawClass, t, preferredName)
    return localizedName === rawClass
        ? rawClass
        : `${rawClass} · ${localizedName}`
}

export const formatRawClassSummary = (
    summary: string | null | undefined,
    t: TranslateRawClass,
    options?: RawClassSummaryFormatOptions,
): string | null => {
    if (!summary) {
        return summary ?? null
    }

    const parts = summary
        .split(',')
        .map((part) => {
            const segment = part.trim()
            if (!segment) {
                return null
            }

            const match = segment.match(SUMMARY_SEGMENT_PATTERN)
            const rawClass = (match?.[1] ?? segment).trim()
            if (!rawClass) {
                return null
            }
            const count = Number(match?.[3] ?? '1')
            const localizedName = resolvePreferredLocalizedName(
                rawClass,
                t,
                options?.localizedNames?.[rawClass.trim().toLowerCase()],
            )
            const displayName = options?.withCode
                ? formatRawClassDisplayName(rawClass, t, localizedName)
                : localizedName
            return `${displayName}(${Number.isFinite(count) ? count : 1})`
        })
        .filter((part): part is string => Boolean(part))

    return parts.length > 0 ? parts.join(', ') : null
}
